import { A, D, G } from '@mobily/ts-belt';
import { Decimal } from 'decimal.js';
import { FieldValues } from 'react-hook-form';
import { match, P } from 'ts-pattern';

import {
  B2bCampaignPaymentMethod,
  B2bCampaignType,
  CampaignAllocations,
  PostCampaignsEndpoint,
  PutB2bCampaignEndpoint,
} from '@Common/types/bo/b2b/campaigns';

import { B2bCampaignOperationPaymentMethodChoice } from '@sh4res/bo-common/components/B2bCampaigns/choices';
import {
  AllocationRecord,
  CompanyCollectedChoice,
  isAllocationAccepted,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import type { VacationDaysAllocationFormRow } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysAllocationRows';
import { VACATION_DAYS_AMOUNT_FIELDS } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysColumns';
import {
  hasAnyVacationDays,
  VACATION_DAYS_FIELDS,
} from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/vacationDaysFile';
import { UploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type TransformParamsBase = {
  isDraft: boolean;
  upload?: UploadPresignedUrl;
  /** Include MC campaign choices with plan selected even when MC amount is still 0 (calculate max). */
  includeEmptyMatchingContributionChoices?: boolean;
};
type TransformedData =
  | PostCampaignsEndpoint['Client']['RequestBody']
  | PutB2bCampaignEndpoint['Client']['RequestBody'];

type Choice = NonNullable<AllocationRecord['company_collected_choices']>[number];

type AmountsByDayType = Record<
  (typeof VACATION_DAYS_AMOUNT_FIELDS)[keyof typeof VACATION_DAYS_AMOUNT_FIELDS],
  number | null
>;

const getAmountsByDayType = (
  dayTypeAllocations: VacationDaysAllocationFormRow['day_type_allocations'],
) =>
  Object.fromEntries(
    Object.entries(VACATION_DAYS_AMOUNT_FIELDS).map(([dayType, field]) => [
      field,
      dayTypeAllocations?.find((entry) => entry.days_type === dayType)?.amount ?? null,
    ]),
  ) as AmountsByDayType;

const getAllocations = ({
  isDraft,
  allocations,
  isMatchingContributionComputedByCompany,
  type,
  action,
  includeEmptyMatchingContributionChoices = false,
}: Pick<TransformParamsBase, 'isDraft' | 'includeEmptyMatchingContributionChoices'> & {
  allocations: AllocationRecord[];
  action: 'edit' | 'create';
  isMatchingContributionComputedByCompany?: boolean;
  type: B2bCampaignType;
}) => {
  if (
    A.some(allocations, (entry) =>
      VACATION_DAYS_FIELDS.some((field) => G.isNotNullable(entry[field])),
    )
  ) {
    return action === 'create'
      ? (A.filterMap(allocations, (entry) =>
          G.isNotNullable(entry.employee_id) && hasAnyVacationDays(entry)
            ? {
                ...D.selectKeys(entry, VACATION_DAYS_FIELDS),
                employee_id: entry.employee_id,
                amount: 0,
              }
            : undefined,
        ) as CampaignAllocations)
      : A.filterMap(allocations, (employee) => {
          const amountsByDayType = getAmountsByDayType(
            (employee as VacationDaysAllocationFormRow).day_type_allocations,
          );
          // The allocation amount is the sum of the per-day-type amounts
          // Summed with Decimal: the backend compares it exactly with the per-type amounts
          const amount = Object.values(amountsByDayType)
            .reduce((total, dayTypeAmount) => total.plus(dayTypeAmount ?? 0), new Decimal(0))
            .toNumber();
          return amount > 0
            ? {
                ...D.selectKeys(employee, ['nir', 'ntt', ...VACATION_DAYS_FIELDS, 'employee_id']),
                amount,
                ...amountsByDayType,
              }
            : undefined;
        });
  }

  return A.filterMap(allocations, (entry) => {
    // For drafts ("save for later"), the employee may not be matched yet (e.g. right after the
    // allocation file upload, before the review step). Forward nir/ntt so the backend can resolve
    // the employee_id, or skip the row if it cannot be matched.
    const base = D.selectKeys(
      entry,
      isDraft
        ? ['amount', 'employee_id', 'company_collected_choices', 'nir', 'ntt']
        : ['amount', 'employee_id', 'company_collected_choices'],
    );
    const result = {
      ...(isDraft ? D.reject(base, G.isNull) : base),
      company_collected_choices: A.reduceWithIndex(
        base.company_collected_choices ?? [],
        [] as Choice[],
        (acc, choice, index) => {
          const formattedChoice = {
            ...D.reject(choice, G.isNull),
            id: isDraft ? `${index + 1}` : choice.id,
            plan_id: extractPlanIdFromChoice(choice),
            matching_contribution_amount: isMatchingContributionComputedByCompany
              ? choice.matching_contribution_amount
              : null,
            ...(isDraft && {
              amount: choice.amount ?? 0,
              // TODO: risk_profile removed from MVP, to be brought back later https://sh4res.atlassian.net/browse/BRAV-8365
              risk_profile: undefined,
            }),
          };

          const hasPositiveAmount =
            (formattedChoice.amount ?? formattedChoice.amount_in_days ?? 0) > 0;
          const isMatchingContributionChoice =
            type === 'matching_contribution' &&
            (includeEmptyMatchingContributionChoices ||
              (formattedChoice.matching_contribution_amount ?? 0) > 0);

          return (hasPositiveAmount || isMatchingContributionChoice) &&
            (!isDraft || G.isNotNullable(formattedChoice.allocation_type))
            ? [...acc, formattedChoice]
            : acc;
        },
      ),
    };
    return isAllocationAccepted({
      campaignType: type,
      amount: result.amount ?? 0,
      companyCollectedChoices: result.company_collected_choices,
    })
      ? result
      : undefined;
  });
};

const baseKeys = [
  'reason',
  'start_date',
  'end_date',
  'fiscal_period_start',
  'fiscal_period_end',
  'amount_type_input',
  'amount_input',
  'matching_contribution_related_cash_source',
  'related_campaign_id',
  'group_operation_gross_amount',
  'is_emails_enabled',
  'reference_year',
] as const;

export function handleCampaignTransform(
  params: TransformParamsBase & { action: 'create' },
): (data: FieldValues) => Promise<PostCampaignsEndpoint['Client']['RequestBody']>;
export function handleCampaignTransform(
  params: TransformParamsBase & { action: 'edit' },
): (data: FieldValues) => Promise<PutB2bCampaignEndpoint['Client']['RequestBody']>;

export function handleCampaignTransform(
  params: TransformParamsBase & { action: 'create' | 'edit' },
) {
  const { isDraft, action, upload, includeEmptyMatchingContributionChoices } = params;

  return async (data: FieldValues): Promise<TransformedData> => {
    const keys =
      action === 'create'
        ? ([...baseKeys, 'type', 'company_id', 'b2b_contract_ids'] as const)
        : ([...baseKeys, 'type'] as const);

    let payload: Partial<TransformedData> = D.selectKeys(data, keys);

    const allocations = G.isNullable(data.allocations)
      ? []
      : getAllocations({
          allocations: data.allocations,
          isDraft,
          action,
          isMatchingContributionComputedByCompany:
            data.matching_contribution_computed_by === 'company',
          type: data.type,
          includeEmptyMatchingContributionChoices,
        });

    if (isDraft) {
      payload = D.reject(payload, G.isNull);
    }

    let distributionFileFields: Partial<TransformedData> = {};
    if (
      data.allocation_computed_by === 'distributor' &&
      data.distribution_rule_file_source &&
      upload
    ) {
      const uploadResult = await upload(data.distribution_rule_file_source.rawFile);
      if (!uploadResult) {
        throw new Error('Error during upload');
      }
      distributionFileFields = {
        distribution_rule_file: {
          presigned_path: uploadResult.path,
          name: data.distribution_rule_file_source.title,
        },
      };
    }

    let allocationsFileFields: Partial<TransformedData> = {};
    if (data.allocations_file?.rawFile && upload) {
      const uploadResult = await upload(data.allocations_file.rawFile);
      if (!uploadResult) {
        throw new Error('Error during upload');
      }
      allocationsFileFields = {
        allocations_file: {
          presigned_path: uploadResult.path,
          name: data.allocations_file.title,
        },
      };
    }

    const isLatePaymentInterests =
      data.is_automatic_late_payment_interests === 'yes' ||
      (G.isNotNullable(data.late_payment_interests_override_amount) &&
        data.late_payment_interests_override_amount > 0);

    const paymentMethod: B2bCampaignPaymentMethod | undefined = match<
      B2bCampaignOperationPaymentMethodChoice | null | undefined,
      B2bCampaignPaymentMethod | undefined
    >(data.payment_method)
      .with('sepa_linked', /* , 'sepa_new',*/ () => 'sepa')
      .with('transfer', () => 'transfer')
      .with(P.nullish, () => undefined)
      .exhaustive();

    // BO Internal agents may edit the processing options on any campaign, whatever its reason, for
    // as long as it has not started. The backend rejects the same fields coming from BO External,
    // so they are only forwarded from BO Internal; it also ignores them once the campaign has
    // started, which is what keeps an ongoing consultation on its original options.
    const processingOptions = isBoExternal()
      ? {}
      : {
          choice_collected_by: data.choice_collected_by,
          payout_by: data.payout_by,
          allocation_computed_by: data.allocation_computed_by,
        };

    return {
      ...payload,
      ...processingOptions,
      ...distributionFileFields,
      ...allocationsFileFields,
      value_sharing_bonus_fiscal_rule: data.value_sharing_bonus_fiscal_rule ?? undefined,
      fiscal_period_start: data.fiscal_period_start ?? null,
      fiscal_period_end: data.fiscal_period_end ?? null,
      allocations,
      new_sepa_rib_doc_file: null,
      is_draft: isDraft,
      payment_method: paymentMethod,
      is_late_payment_interests: isLatePaymentInterests,
      late_payment_interests_override_amount:
        data.late_payment_interests_override_amount > 0
          ? data.late_payment_interests_override_amount
          : null,
    } as TransformedData;
  };
}

function extractPlanIdFromChoice(
  choice: CompanyCollectedChoice,
): number | 'payout' | 'ccb' | null | undefined {
  if (choice.allocation_type === 'payout') {
    return undefined;
  }

  if (choice.id?.includes('ccb')) {
    const [_ccb, planId] = choice.id.split('_');
    return parseInt(planId);
  }

  return choice.plan_id;
}

export const extractPlanId = (
  choice: CompanyCollectedChoice,
  isChoiceConcernCcb: boolean,
): number | 'payout' | 'ccb' | null | undefined => {
  if (isChoiceConcernCcb) {
    return 'ccb';
  }
  return choice.allocation_type === 'payout' ? 'payout' : choice.plan_id;
};
